import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { PackageMinus, Loader2, Save } from "lucide-react";
import api, { formatMoney, formatMoney2, formatNumber, formatApiErrorDetail } from "../../lib/api";

export const IncentivoStockD = ({ importId, names = {} }) => {
  const [data, setData] = useState(null);
  const [pct, setPct] = useState("");
  const [saving, setSaving] = useState(false);
  const load = () => api.get(`/sales/incentive-stock-d/${importId}`).then((r) => { setData(r.data); setPct(String(r.data.pct)); }).catch(() => setData(null));
  useEffect(() => { if (importId) load(); }, [importId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/sales/incentive-stock-d", { pct: Number(String(pct).replace(",", ".")) || 0 });
      toast.success("Incentivo de stock D guardado");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "El % debe estar entre 0 y 50");
    } finally { setSaving(false); }
  };

  if (!data) return null;
  return (
    <div className="card p-6 mt-6" data-testid="incentivo-stock-d">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1"><PackageMinus size={12} /> Incentivo independiente</div>
          <h3 className="font-display text-xl font-bold">Incentivo por stock D vendido</h3>
          <p className="text-xs text-slate-500 mt-1 max-w-xl">
            Cada empleado cobra el % que elijas sobre lo que factura de stock D <b>sin ventas en un año y con la última entrada de hace más
            de un año</b> (según tu estudio de stock; sin fecha de entrada no cuenta). No se mezcla con el plan de incentivos de arriba.
          </p>
        </div>
        <form onSubmit={save} className="flex items-center gap-2">
          <div className="relative">
            <input className="input text-right" style={{ width: 96, paddingRight: 28 }} inputMode="decimal" value={pct} onChange={(e) => setPct(e.target.value)} data-testid="stock-d-pct-input" />
            <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-400">%</span>
          </div>
          <button className="btn btn-primary" disabled={saving} data-testid="stock-d-pct-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Guardar
          </button>
        </form>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div className="rounded-lg bg-slate-50 p-3"><div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Cuenta para incentivo · {data.label}</div><div className="font-display text-xl font-extrabold" data-testid="stock-d-total-vendido">{formatMoney(data.total_vendido)}</div><div className="text-[11px] text-slate-500">de {formatMoney(data.total_d)} de stock D vendido</div></div>
        <div className="rounded-lg bg-slate-50 p-3"><div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">% a repartir</div><div className="font-display text-xl font-extrabold">{String(data.pct).replace(".", ",")} %</div></div>
        <div className="rounded-lg bg-emerald-50 p-3"><div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Total incentivo</div><div className="font-display text-xl font-extrabold text-emerald-700" data-testid="stock-d-total-incentivo">{formatMoney2(data.total_incentivo)}</div></div>
      </div>
      {data.empleados.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="stock-d-vacio">Este mes no se ha vendido stock de clase D (o no hay estudio de stock de referencia).</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="dense" data-testid="stock-d-tabla">
            <thead><tr><th>Empleado</th><th className="num">Stock D vendido</th><th className="num">Cuenta para incentivo</th><th className="num">Uds</th><th className="num">Productos</th><th>Lo que más ha movido</th><th className="num">Incentivo</th></tr></thead>
            <tbody>
              {data.empleados.map((e, i) => (
                <tr key={e.vendedor} data-testid={`stock-d-fila-${i}`}>
                  <td className="font-semibold">{names[e.vendedor] || e.vendedor}</td>
                  <td className="num text-slate-500">{formatMoney2(e.facturacion_d)}</td>
                  <td className="num font-semibold">{formatMoney2(e.facturacion)}</td>
                  <td className="num">{formatNumber(e.uds)}</td>
                  <td className="num">{formatNumber(e.productos)}</td>
                  <td className="text-xs text-slate-500 max-w-xs truncate" title={e.top.map((p) => p.descripcion).join(" · ")}>{e.top.map((p) => p.descripcion).join(" · ")}</td>
                  <td className="num font-bold text-emerald-700" data-testid={`stock-d-incentivo-${i}`}>{formatMoney2(e.incentivo)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
